import React, { useMemo } from 'react';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { Subscription } from '@domain/subscription/models/SubscriptionPlan';
import { ClientType } from '@domain/connection/models/Connector';
import { useConnections } from '@shared/hooks';
import { Icon } from '@shared/icons';
import { APP_CONFIG } from '../../../../../config/app.config';
import { PlanHeader } from './PlanHeader';

const colors = APP_CONFIG.ui.theme.colors;
const SHOPIFY_LOGO = '/assets/images/shopify-logo.png';

function normalizeShopDomain(shop: string): string {
  return shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;
}

function formatShopLabel(shopDomain: string): string {
  return shopDomain.replace(/\.myshopify\.com$/i, '');
}

function shopifyAdminAppsUrl(shopDomain: string): string {
  return `https://admin.shopify.com/store/${formatShopLabel(shopDomain)}/settings/apps`;
}

interface ShopifySubscriptionSectionProps {
  subscription: Subscription;
  onChangePlan: () => void;
}

export const ShopifySubscriptionSection: React.FC<ShopifySubscriptionSectionProps> = ({
  subscription,
  onChangePlan,
}) => {
  const { connections } = useConnections();

  const shopifyConnection = useMemo(
    () => connections?.find(c => c.connector.type === ClientType.SHOPIFY),
    [connections],
  );

  const linkedShopifyShop = useMemo(() => {
    if (subscription.shopifyShopDomain) {
      return normalizeShopDomain(subscription.shopifyShopDomain);
    }
    if (shopifyConnection?.name) {
      return normalizeShopDomain(shopifyConnection.name);
    }
    return undefined;
  }, [subscription.shopifyShopDomain, shopifyConnection?.name]);

  const shopifyLogo = shopifyConnection?.connector.image || SHOPIFY_LOGO;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={5}
      alignItems={{ xs: 'stretch', md: 'center' }}
    >
      <PlanHeader
        name={subscription.plan.name}
        status={subscription.status}
        description={subscription.plan.description}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          bgcolor: colors.background,
          flexWrap: 'wrap',
          minHeight: 75,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 43,
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            flexShrink: 0,
            bgcolor: colors.background,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={shopifyLogo}
            alt="Shopify"
            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </Box>

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 180 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '20px',
              color: colors.primaryText,
            }}
          >
            Your subscription is managed in Shopify
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              color: colors.primaryText,
              wordBreak: 'break-word',
            }}
          >
            {linkedShopifyShop ?? 'No store linked'}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon icon="ArrowRefresh03" />}
            onClick={onChangePlan}
          >
            Change plan
          </Button>

          {linkedShopifyShop && (
            <Button
              variant="outlined"
              size="small"
              component={Link}
              href={shopifyAdminAppsUrl(linkedShopifyShop)}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Icon icon="LinkExternal" />}
            >
              Manage billing
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};
